import React, { useState } from "react";
import PropTypes from "prop-types";
import Badge from "../../components/ui-components/Badge.jsx";
import AnalyzerPeriodSelector from "../cost-analyzer/AnalyzerPeriodSelector.jsx";
import { secondaryButtonCls } from "../../components/ui-components/buttonStyles.js";

// Shared report details + recipients fields, used by both the create form (BoardReportConfig) and
// the Settings tab of the report home (panels/SettingsPanel) so the two never drift. Controlled:
// the parent owns name / period range / recipients state, validation and saving; this component owns
// only the transient recipient-input state.
//
// Width is applied via inline style (width:100%) rather than `w-full`, a no-op under this project's
// overridden theme.width scale (CLAUDE.md reference).
const inputCls =
  "p-2.5 border border-border rounded-ctrl text-sm bg-card text-text placeholder:text-text-3 focus:outline-none focus:border-primary transition-colors";
const FULL_WIDTH = { width: "100%" };

const ReportDetailsFields = ({
  name,
  onNameChange,
  rangeStart,
  rangeEnd,
  setRangeStart,
  setRangeEnd,
  recipients,
  onRecipientsChange,
  errors = {},
}) => {
  const [emailInput, setEmailInput] = useState("");
  const [nameInput, setNameInput] = useState("");

  const addRecipient = () => {
    const email = emailInput.trim();
    if (!email) return;
    onRecipientsChange([...recipients, { email, name: nameInput.trim() }]);
    setEmailInput("");
    setNameInput("");
  };

  const removeRecipient = (index) => {
    onRecipientsChange(recipients.filter((_, i) => i !== index));
  };

  const onRecipientKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addRecipient();
    }
  };

  return (
    <>
      <div className="bg-card border border-card-bd rounded-card p-6 space-y-5">
        <div>
          <label htmlFor="board-report-name" className="block text-sm font-medium text-text mb-1.5">
            Name
          </label>
          <input
            id="board-report-name"
            type="text"
            className={inputCls}
            style={FULL_WIDTH}
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="e.g. Monthly board pack"
          />
          <p className="mt-1.5 text-xs text-text-3">Shown on the cover page and in your report list.</p>
          {errors.name && <div className="text-danger text-xs mt-1">{errors.name}</div>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <span className="block text-sm font-medium text-text mb-1.5">Type</span>
            <div className="flex items-center gap-2 h-[42px]">
              <Badge status="info" label="Monthly Summary" />
            </div>
            <p className="mt-1.5 text-xs text-text-3">The flagship monthly board pack.</p>
          </div>

          <div>
            <span className="block text-sm font-medium text-text mb-1.5">Period</span>
            {rangeStart && rangeEnd && (
              <AnalyzerPeriodSelector
                startDate={rangeStart}
                endDate={rangeEnd}
                setStartDate={setRangeStart}
                setEndDate={setRangeEnd}
              />
            )}
            <p className="mt-1.5 text-xs text-text-3">The report covers the month of the selected period.</p>
            {errors.period && <div className="text-danger text-xs mt-1">{errors.period}</div>}
          </div>
        </div>
      </div>

      <div className="bg-card border border-card-bd rounded-card p-6 space-y-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-text">Recipients</span>
            <span className="text-[11px] font-medium uppercase tracking-wide text-text-3">Optional</span>
          </div>
          <p className="mt-1 text-sm text-text-2">
            Who receives the PDF when the report is sent. Add or change these any time.
          </p>
        </div>

        {recipients.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {recipients.map((r, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <li
                key={`${r.email}-${index}`}
                className="inline-flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full bg-background-grey border border-border text-sm text-text"
              >
                <span>{r.name ? `${r.name} <${r.email}>` : r.email}</span>
                <button
                  type="button"
                  className="inline-flex items-center justify-center h-[20px] w-[20px] rounded-full text-text-3 hover:text-danger hover:bg-card cursor-pointer transition-colors"
                  onClick={() => removeRecipient(index)}
                  aria-label={`Remove recipient ${r.email}`}
                >
                  <span aria-hidden="true">✕</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <label htmlFor="recipient-email" className="block text-xs font-medium text-text-2 mb-1.5">
              Email
            </label>
            <input
              id="recipient-email"
              type="text"
              className={inputCls}
              style={FULL_WIDTH}
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={onRecipientKeyDown}
              placeholder="name@company.com"
            />
          </div>
          <div>
            <label htmlFor="recipient-name" className="block text-xs font-medium text-text-2 mb-1.5">
              Name (optional)
            </label>
            <input
              id="recipient-name"
              type="text"
              className={inputCls}
              style={FULL_WIDTH}
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={onRecipientKeyDown}
              placeholder="Jane Doe"
            />
          </div>
          <button type="button" className={secondaryButtonCls} onClick={addRecipient}>
            Add recipient
          </button>
        </div>
        {errors.recipients && <div className="text-danger text-xs mt-1">{errors.recipients}</div>}
      </div>
    </>
  );
};

ReportDetailsFields.propTypes = {
  name: PropTypes.string.isRequired,
  onNameChange: PropTypes.func.isRequired,
  rangeStart: PropTypes.object,
  rangeEnd: PropTypes.object,
  setRangeStart: PropTypes.func.isRequired,
  setRangeEnd: PropTypes.func.isRequired,
  recipients: PropTypes.arrayOf(PropTypes.shape({ email: PropTypes.string, name: PropTypes.string })).isRequired,
  onRecipientsChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
};

export default ReportDetailsFields;
